if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // On lie la méthode onVariantChange pour s'assurer que 'this' fait référence à l'instance de MediaGallery
        this.onVariantChangeBound = this.onVariantChange.bind(this);

        // Ces écouteurs peuvent rester dans le constructeur si les éléments existent déjà,
        // car ils sont sur des éléments enfants directs de ce Custom Element et
        // sont donc disponibles dès la création de l'instance.
        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }
      }

      // Appelé lorsque l'élément est inséré dans le DOM
      connectedCallback() {
        // C'est ici que nous nous abonnons aux événements globaux
        document.addEventListener('variant:change', this.onVariantChangeBound);

        // Réinitialiser les sliders si nécessaire, car le DOM a été remplacé
        this.initializeSliders(); // Nouvelle méthode pour gérer l'initialisation

        // Appelle removeListSemantic si le layout desktop inclut des miniatures et que l'écran est large
        // C'est mieux de le faire ici car cela dépend de l'élément étant dans le DOM
        if (this.elements.thumbnails && this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      // Appelé lorsque l'élément est retiré du DOM
      disconnectedCallback() {
        // C'est crucial pour désabonner les événements afin d'éviter les fuites de mémoire
        document.removeEventListener('variant:change', this.onVariantChangeBound);
      }

      // Nouvelle méthode pour gérer l'initialisation/réinitialisation des sliders
      initializeSliders() {
          // Vérifiez si les sliders sont des instances de SliderComponent et si elles ont une méthode 'init'
          // Le constructeur de SliderComponent devrait gérer l'essentiel, mais parfois une méthode explicite est utile.
          if (this.elements.viewer && typeof this.elements.viewer.init === 'function') {
              this.elements.viewer.init();
          }
          if (this.elements.thumbnails && typeof this.elements.thumbnails.init === 'function') {
              this.elements.thumbnails.init();
          }
          // Si votre SliderComponent n'a pas de méthode 'init()' publique,
          // vous pouvez être obligé de simplement faire confiance à son `connectedCallback`.
          // Mais l'erreur 'offsetLeft' suggère que ce n'est pas suffisant dans ce cas.
      }


      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          if (activeMedia.parentElement.firstChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          // `this.elements.viewer.slider` pourrait être `undefined` après un remplacement si le `slider-component`
          // n'est pas bien réinitialisé, ce qui est la cause probable de l'erreur `offsetLeft`.
          // Assurez-vous que slider est une propriété du custom element slider-component.
          if (this.elements.viewer && this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            // C'est ici que l'erreur `offsetLeft` se produit probablement si `activeMedia`
            // n'a pas encore de parent ou n'est pas correctement rendu dans le DOM.
            // Le remplacement de la section devrait garantir que `activeMedia` est valide,
            // mais le timing avec `slider.isSlideVisible` peut être délicat.
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (this.elements.liveRegion && activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        // L'erreur `isSlideVisible` est dans `global.js:798`.
        // C'est probablement la méthode `isSlideVisible` de `slider-component` qui pose problème.
        // Si `this.elements.thumbnails.isSlideVisible` est `undefined` après le remplacement,
        // c'est que la nouvelle instance de `slider-component` n'a pas été correctement initialisée.
        if (this.elements.thumbnails.isSlideVisible && !this.elements.thumbnails.isSlideVisible(thumbnail, 10)) {
            if (this.elements.thumbnails.slider) {
                this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
            }
        } else if (!this.elements.thumbnails.isSlideVisible) {
            if (this.elements.thumbnails.slider) {
                this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
            }
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;

        const announce = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
          image.removeEventListener('load', announce);
        };

        if (image.complete) {
          announce();
        } else {
          image.addEventListener('load', announce);
        }
      }

      playActiveMedia(activeItem) {
        if (window.pauseAllMedia) {
          window.pauseAllMedia();
        }
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia && deferredMedia.loadContent) {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer || !this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        if (this.elements.viewer.sliderItems) {
          this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
        }
      }

      async onVariantChange(event) {
        const newVariantId = event.detail?.variant?.id;
        let sectionIdToReload = this.dataset.mainProductSectionId;

        if (!sectionIdToReload) {
          const sectionElement = this.closest('.shopify-section');
          if (!sectionElement) {
            console.error('MediaGallery: Parent Shopify section not found. Cannot reload.');
            return;
          }
          sectionIdToReload = sectionElement.dataset.sectionId;
        }

        if (sectionIdToReload) {
          try {
            let url = `${window.location.pathname}?sections=${sectionIdToReload}`;
            if (newVariantId) {
              url += `&variant=${newVariantId}`;
            }

            // Option 1: Ajouter une classe pour masquer pendant le rechargement
            // Ceci peut aider à lisser le clignotement. Vous devrez ajouter du CSS :
            // media-gallery.is-reloading { opacity: 0; transition: opacity 0.1s ease-out; }
            // Ou plus simple : masquer la galerie, puis l'afficher.
            // this.style.opacity = '0'; // Masque temporairement la galerie actuelle

            const response = await fetch(url);
            const data = await response.json();

            if (data[sectionIdToReload]) {
              const newSectionHtml = data[sectionIdToReload];
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = newSectionHtml;

              const newSectionElement = tempDiv.querySelector(`#shopify-section-${sectionIdToReload}`);

              if (newSectionElement) {
                const currentSectionElement = document.querySelector(`#shopify-section-${sectionIdToReload}`);
                if (currentSectionElement) {
                  currentSectionElement.replaceWith(newSectionElement);

                  // 1. Déclenchement de l'événement Shopify natif (c'est la meilleure pratique)
                  document.dispatchEvent(new CustomEvent('shopify:section:load', {
                      bubbles: true,
                      detail: {
                          sectionId: sectionIdToReload,
                          html: newSectionElement.innerHTML
                      }
                  }));

                  // 2. Recherche et appel de la méthode de mise à jour sur le nouveau composant 'MediaGallery'
                  // Le DOM a été remplacé. Nous devons cibler la *nouvelle* instance de media-gallery.
                  const newMediaGallery = newSectionElement.querySelector('media-gallery');

                  if (newMediaGallery) {
                      // Si votre Lightbox est un enfant direct du DOM global ou dans un autre composant,
                      // il est crucial qu'elle se mette à jour.
                      
                      // Si elle ne le fait pas via shopify:section:load (ce qui est rare),
                      // on peut tenter de la cibler directement :
                      const mediaModal = document.querySelector('product-media-modal'); 
                      
                      if (mediaModal && typeof mediaModal.updateMedia === 'function') {
                          // Cette méthode doit exister dans votre fichier JS de la Lightbox
                          mediaModal.updateMedia();
                      }
                  }
                } else {
                  console.error(`MediaGallery: Current section element with ID #shopify-section-${sectionIdToReload} not found in DOM.`);
                }
              } else {
                console.error(`MediaGallery: New section element with ID #shopify-section-${sectionIdToReload} not found in fetched HTML. Check section rendering.`);
              }
            } else {
              console.error(`MediaGallery: Section data for ID ${sectionIdToReload} not found in response. Ensure the section is correctly requested.`);
            }
          } catch (error) {
            console.error('MediaGallery: Error reloading section on variant change:', error);
          } finally {
            // if (this.style) { // Assurez-vous que 'this' est toujours une référence valide
            //   this.style.opacity = ''; // Rétablit l'opacité
            // }
          }
        }
      }
    }
  );
}